/**
 * CreateShipmentPage — multi-step shipment-creation form. Mirrors Flutter
 * `create_shipment_page.dart` (5 steps): Receiver → Package → Locations
 * → Pricing → Payment.
 *
 * Phase 1 ships a working but trimmed version: the locations step accepts
 * city + address text only (map picker lands in Phase 1.5 once Google
 * Maps key is wired). Pricing step lets the sender either set a fixed
 * price OR `waitForOffers=true` to let drivers bid — same backend
 * contract as the Flutter app.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight, ChevronLeft, User, Phone, Package, MapPin, DollarSign, Wallet,
} from 'lucide-react';
import { useCreateShipment } from '@/hooks/useShipments';
import { AppLayout } from '@/components/layout/AppLayout';
import { Button } from '@/components/common/Button';
import { TextField } from '@/components/common/TextField';

interface FormState {
  receiverName: string;
  receiverPhone: string;
  packageDescription: string;
  weight: string;
  isFragile: boolean;
  requiresRefrigeration: boolean;
  pickupCity: string;
  pickupAddress: string;
  receiverCity: string;
  receiverAddress: string;
  pickupMethod: 'door' | 'center';
  deliveryMethod: 'door' | 'center';
  price: string;
  waitForOffers: boolean;
  currency: string;
  paymentMethod: 'cash' | 'wallet';
  paidBy: 'sender' | 'receiver';
}

const INITIAL: FormState = {
  receiverName: '',
  receiverPhone: '',
  packageDescription: '',
  weight: '',
  isFragile: false,
  requiresRefrigeration: false,
  pickupCity: '',
  pickupAddress: '',
  receiverCity: '',
  receiverAddress: '',
  pickupMethod: 'door',
  deliveryMethod: 'door',
  price: '',
  waitForOffers: false,
  currency: 'USD',
  paymentMethod: 'cash',
  paidBy: 'sender',
};

const STEPS = ['receiver', 'package', 'locations', 'pricing', 'payment'] as const;
type StepKey = typeof STEPS[number];

// `LucideIcon` exports as ForwardRefExoticComponent; using `LucideIcon`
// keeps the type aligned with the actual lucide-react export.
const STEP_TITLES: Record<StepKey, { ar: string; icon: typeof User }> = {
  receiver:  { ar: 'المستلِم',  icon: User },
  package:   { ar: 'الطرد',     icon: Package },
  locations: { ar: 'المواقع',   icon: MapPin },
  pricing:   { ar: 'التسعير',  icon: DollarSign },
  payment:   { ar: 'الدفع',    icon: Wallet },
};

export default function CreateShipmentPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const createMut = useCreateShipment();
  const [form, setForm] = useState<FormState>(INITIAL);
  const [stepIdx, setStepIdx] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const step = STEPS[stepIdx];

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((s) => ({ ...s, [key]: value }));
  }

  function validateStep(): string | null {
    if (step === 'receiver') {
      if (!form.receiverName.trim()) return t('err_receiver_name', 'اكتب اسم المستلِم');
      const cleaned = form.receiverPhone.replace(/\s+/g, '');
      if (!/^\+?\d{8,15}$/.test(cleaned)) return t('err_receiver_phone', 'رقم هاتف المستلِم غير صحيح');
    }
    if (step === 'package') {
      if (!form.packageDescription.trim()) return t('err_description', 'اكتب وصف الطرد');
      const w = parseFloat(form.weight);
      if (!w || w <= 0) return t('err_weight', 'أدخل وزناً صحيحاً');
    }
    if (step === 'locations') {
      if (!form.pickupCity.trim() || !form.receiverCity.trim()) {
        return t('err_locations', 'حدّد مدينة الاستلام والتسليم');
      }
    }
    if (step === 'pricing') {
      if (!form.waitForOffers) {
        const p = parseFloat(form.price);
        if (!p || p <= 0) return t('err_price', 'أدخل سعراً صحيحاً أو فعّل العروض');
      }
    }
    return null;
  }

  function next() {
    setError(null);
    const err = validateStep();
    if (err) { setError(err); return; }
    if (stepIdx < STEPS.length - 1) setStepIdx(stepIdx + 1);
    else void submit();
  }

  function back() {
    setError(null);
    if (stepIdx > 0) setStepIdx(stepIdx - 1);
    else navigate(-1);
  }

  async function submit() {
    setError(null);
    try {
      const created = await createMut.mutateAsync({
        receiverName: form.receiverName.trim(),
        receiverPhone: form.receiverPhone.replace(/\s+/g, ''),
        packageDescription: form.packageDescription.trim(),
        weight: parseFloat(form.weight),
        isFragile: form.isFragile,
        requiresRefrigeration: form.requiresRefrigeration,
        pickupCity: form.pickupCity.trim(),
        pickupAddress: form.pickupAddress.trim() || undefined,
        receiverCity: form.receiverCity.trim(),
        receiverAddress: form.receiverAddress.trim() || undefined,
        pickupMethod: form.pickupMethod,
        deliveryMethod: form.deliveryMethod,
        price: form.waitForOffers ? undefined : parseFloat(form.price),
        waitForOffers: form.waitForOffers,
        currency: form.currency,
        paymentMethod: form.paymentMethod,
        paidBy: form.paidBy,
      });
      navigate(`/shipments/${created.id}`, { replace: true });
    } catch (e: any) {
      setError(e?.message || t('create_failed', 'فشل إنشاء الشحنة'));
    }
  }

  const StepIcon = STEP_TITLES[step].icon;

  return (
    <AppLayout>
      {/* Stepper */}
      <div className="flex items-center justify-between mb-6 overflow-x-auto">
        {STEPS.map((s, i) => {
          const Icon = STEP_TITLES[s].icon;
          const active = i === stepIdx;
          const done = i < stepIdx;
          return (
            <div key={s} className="flex items-center gap-2 flex-1 min-w-fit">
              <div
                className={`w-9 h-9 rounded-full flex items-center justify-center transition ${
                  active ? 'bg-primary text-white' : done ? 'bg-success text-white' : 'bg-border text-text-tertiary'
                }`}
              >
                <Icon size={16} />
              </div>
              <span className={`text-xs ${active ? 'font-bold text-primary' : 'text-text-tertiary'}`}>
                {t(`step.${s}`, STEP_TITLES[s].ar)}
              </span>
              {i < STEPS.length - 1 && <div className={`flex-1 h-px ${done ? 'bg-success' : 'bg-border'}`} />}
            </div>
          );
        })}
      </div>

      <div className="bg-surface border border-border rounded-2xl p-6 max-w-xl mx-auto">
        <h2 className="font-bold text-lg text-primary mb-5 flex items-center gap-2">
          <StepIcon size={20} /> {t(`step.${step}`, STEP_TITLES[step].ar)}
        </h2>

        {error && (
          <div className="bg-error/10 text-error rounded-xl p-3 text-sm mb-4">{error}</div>
        )}

        {step === 'receiver' && (
          <div className="space-y-4">
            <TextField
              label={t('receiver_name', 'اسم المستلِم')}
              value={form.receiverName}
              onChange={(e) => update('receiverName', e.target.value)}
              autoFocus
            />
            <TextField
              label={t('receiver_phone', 'هاتف المستلِم')}
              type="tel"
              dir="ltr"
              placeholder="+963 9XX XXX XXX"
              value={form.receiverPhone}
              onChange={(e) => update('receiverPhone', e.target.value)}
            />
          </div>
        )}

        {step === 'package' && (
          <div className="space-y-4">
            <TextField
              label={t('description', 'وصف الطرد')}
              value={form.packageDescription}
              onChange={(e) => update('packageDescription', e.target.value)}
              autoFocus
            />
            <TextField
              label={t('weight_kg', 'الوزن (كغ)')}
              type="number"
              step="0.1"
              dir="ltr"
              value={form.weight}
              onChange={(e) => update('weight', e.target.value)}
            />
            <div className="flex flex-wrap gap-2">
              <Toggle
                label={t('fragile', 'قابل للكسر')}
                value={form.isFragile}
                onChange={(v) => update('isFragile', v)}
              />
              <Toggle
                label={t('requires_cooling', 'يحتاج تبريد')}
                value={form.requiresRefrigeration}
                onChange={(v) => update('requiresRefrigeration', v)}
              />
            </div>
          </div>
        )}

        {step === 'locations' && (
          <div className="space-y-5">
            <fieldset className="space-y-3">
              <legend className="text-sm font-semibold text-text-secondary mb-1">{t('from', 'من')}</legend>
              <TextField
                label={t('city', 'المدينة')}
                value={form.pickupCity}
                onChange={(e) => update('pickupCity', e.target.value)}
              />
              <TextField
                label={t('address', 'العنوان (اختياري)')}
                value={form.pickupAddress}
                onChange={(e) => update('pickupAddress', e.target.value)}
              />
              <RadioGroup
                value={form.pickupMethod}
                onChange={(v) => update('pickupMethod', v as 'door' | 'center')}
                options={[
                  { value: 'door', label: t('door', 'من الباب') },
                  { value: 'center', label: t('center', 'من مركز') },
                ]}
              />
            </fieldset>
            <fieldset className="space-y-3">
              <legend className="text-sm font-semibold text-text-secondary mb-1">{t('to', 'إلى')}</legend>
              <TextField
                label={t('city', 'المدينة')}
                value={form.receiverCity}
                onChange={(e) => update('receiverCity', e.target.value)}
              />
              <TextField
                label={t('address', 'العنوان (اختياري)')}
                value={form.receiverAddress}
                onChange={(e) => update('receiverAddress', e.target.value)}
              />
              <RadioGroup
                value={form.deliveryMethod}
                onChange={(v) => update('deliveryMethod', v as 'door' | 'center')}
                options={[
                  { value: 'door', label: t('door', 'إلى الباب') },
                  { value: 'center', label: t('center', 'إلى مركز') },
                ]}
              />
            </fieldset>
          </div>
        )}

        {step === 'pricing' && (
          <div className="space-y-4">
            <Toggle
              label={t('wait_for_offers', 'استقبل عروضاً من السائقين')}
              value={form.waitForOffers}
              onChange={(v) => update('waitForOffers', v)}
            />
            {!form.waitForOffers && (
              <>
                <TextField
                  label={t('your_price', 'السعر الذي تعرضه')}
                  type="number"
                  step="0.01"
                  dir="ltr"
                  value={form.price}
                  onChange={(e) => update('price', e.target.value)}
                />
                <RadioGroup
                  value={form.currency}
                  onChange={(v) => update('currency', v)}
                  options={[
                    { value: 'USD', label: 'USD' },
                    { value: 'SYP', label: 'SYP' },
                  ]}
                />
              </>
            )}
          </div>
        )}

        {step === 'payment' && (
          <div className="space-y-4">
            <RadioGroup
              label={t('payment_method', 'طريقة الدفع')}
              value={form.paymentMethod}
              onChange={(v) => update('paymentMethod', v as 'cash' | 'wallet')}
              options={[
                { value: 'cash',   label: t('cash', 'كاش') },
                { value: 'wallet', label: t('wallet', 'محفظة') },
              ]}
            />
            <RadioGroup
              label={t('paid_by', 'يدفع')}
              value={form.paidBy}
              onChange={(v) => update('paidBy', v as 'sender' | 'receiver')}
              options={[
                { value: 'sender',   label: t('sender', 'المرسِل') },
                { value: 'receiver', label: t('receiver', 'المستلِم') },
              ]}
              disabled={form.paymentMethod === 'wallet'}
            />
          </div>
        )}

        <div className="flex gap-2 mt-6">
          <Button variant="ghost" onClick={back}>
            <ArrowRight size={16} />
            {stepIdx === 0 ? t('cancel', 'إلغاء') : t('previous', 'السابق')}
          </Button>
          <Button onClick={next} loading={createMut.isPending} fullWidth>
            {stepIdx < STEPS.length - 1 ? (
              <>{t('next', 'التالي')} <ChevronLeft size={16} /></>
            ) : (
              <><Phone size={16} /> {t('create_shipment', 'إنشاء الشحنة')}</>
            )}
          </Button>
        </div>
      </div>
    </AppLayout>
  );
}

function Toggle({ label, value, onChange }: { label: string; value: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!value)}
      className={`flex items-center gap-2 px-3 py-2 rounded-xl border transition text-sm ${
        value ? 'bg-primary text-white border-primary' : 'bg-surface text-text-secondary border-border'
      }`}
    >
      <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${value ? 'bg-white border-white' : 'border-text-tertiary'}`}>
        {value && <span className="w-2 h-2 rounded-full bg-primary" />}
      </span>
      {label}
    </button>
  );
}

function RadioGroup({
  label, value, onChange, options, disabled,
}: {
  label?: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  disabled?: boolean;
}) {
  return (
    <div>
      {label && <div className="text-sm font-medium text-text-secondary mb-2">{label}</div>}
      <div className="flex flex-wrap gap-2">
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            disabled={disabled}
            onClick={() => onChange(o.value)}
            className={`px-4 py-2 rounded-xl border text-sm transition ${
              value === o.value
                ? 'bg-primary text-white border-primary'
                : 'bg-surface text-text-secondary border-border hover:border-primary'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}
